import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ContentLanguageCode, FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { Exhibition } from '@/shared/types';
import { uploadImageToStorage } from '@/shared/utils/imageUtils';
import { validateExhibition } from '@/shared/utils/validation';
import { buildImageAlt } from './imageAlt';

export type ExhibitionTranslations = Record<
  ContentLanguageCode,
  { title: string; description: string; /** Description de la vignette. */ coverAlt?: string }
>;

export type SaveExhibitionResult =
  | { ok: true; coverUrl: string; exhibition: Exhibition | null; didUploadImage: boolean }
  | { ok: false; code: 'database' }
  | { ok: false; code: 'validation'; errors: string[] };

export interface SaveExhibitionParams {
  museumId: string;
  exhibitionId: string;
  exhibition: Exhibition | null;
  selectedImage: File | null;
  /** Vignette supprimée dans le formulaire (sans nouvelle image). */
  coverRemoved?: boolean;
  activeLanguages: ContentLanguageCode[];
  translations: ExhibitionTranslations;
  statusByLanguage: Record<string, 'draft' | 'published'>;
  color: string;
  order: number;
}

const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return value;
  }
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
};

export const saveExhibition = async ({
  museumId,
  exhibitionId,
  exhibition,
  selectedImage,
  coverRemoved = false,
  activeLanguages,
  translations,
  statusByLanguage,
  color,
  order,
}: SaveExhibitionParams): Promise<SaveExhibitionResult> => {
  const db = getFirebaseDb();
  if (!db) {
    return { ok: false, code: 'database' };
  }

  let coverUrl = coverRemoved ? '' : exhibition?.cover || '';
  const didUploadImage = Boolean(selectedImage);
  if (selectedImage) {
    const imagePath = `museums/${museumId}/exhibitions/${exhibitionId}/cover_${Date.now()}_${selectedImage.name}`;
    coverUrl = await uploadImageToStorage(selectedImage, imagePath);
  }

  const nameTranslations: Record<string, string> = {};
  const descriptionTranslations: Record<string, string> = {};
  const statusByLang: Record<string, 'draft' | 'published'> = {};

  activeLanguages.forEach((lang) => {
    nameTranslations[lang] = translations[lang]?.title || '';
    descriptionTranslations[lang] = translations[lang]?.description || '';
    statusByLang[lang] = statusByLanguage[lang] || 'draft';
  });

  const validation = validateExhibition({
    name: nameTranslations,
    description: descriptionTranslations,
    status: statusByLang,
  });

  if (!validation.isValid) {
    return { ok: false, code: 'validation', errors: validation.errors };
  }

  const now = new Date();
  const updatedAtByLang: Record<string, Date> = {};
  activeLanguages.forEach((lang) => {
    updatedAtByLang[lang] = now;
  });

  const existingCreatedAt = exhibition?.createdAt;
  const createdAtByLang: Record<string, Date> = {};
  if (existingCreatedAt) {
    Object.keys(existingCreatedAt).forEach((lang) => {
      const existingDate = toDate(existingCreatedAt[lang]);
      if (existingDate) {
        createdAtByLang[lang] = existingDate;
      }
    });
  }
  activeLanguages.forEach((lang) => {
    if (!createdAtByLang[lang]) {
      createdAtByLang[lang] = now;
    }
  });

  const exhibitionRef = doc(
    db,
    FIRESTORE_COLLECTIONS.MUSEUMS,
    museumId,
    FIRESTORE_COLLECTIONS.EXHIBITIONS,
    exhibitionId
  );
  await updateDoc(exhibitionRef, {
    name: nameTranslations,
    description: descriptionTranslations,
    cover: coverUrl,
    coverAlt: buildImageAlt({
      existing: exhibition?.coverAlt,
      imageAlt: Object.fromEntries(activeLanguages.map((lang) => [lang, translations[lang]?.coverAlt || ''])),
      activeLanguages,
      hasImage: Boolean(coverUrl),
    }),
    status: statusByLang,
    color,
    order,
    createdAt: createdAtByLang,
    updatedAt: updatedAtByLang,
  });

  const updatedDoc = await getDoc(exhibitionRef);
  if (updatedDoc.exists()) {
    return {
      ok: true,
      coverUrl,
      didUploadImage,
      exhibition: {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      } as Exhibition,
    };
  }

  return { ok: true, coverUrl, didUploadImage, exhibition: null };
};
